import type React from 'react'
import {forwardRef} from 'react'
import {clsx} from 'clsx'
import styles from './ToggleButton.module.css'

interface ToggleButtonProps {
  icon?: React.ReactNode
  isSelected?: boolean
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void
  hasMenu?: boolean
  isMenuOpen?: boolean
  selectedItemLabel?: string
  children: React.ReactNode
}

export const ToggleButton = forwardRef<HTMLButtonElement, ToggleButtonProps>(
  ({icon, isSelected = false, onClick, hasMenu = false, isMenuOpen = false, selectedItemLabel, children}, ref) => {
    // Determine the appropriate aria attributes based on component state
    const getAriaAttributes = () => {
      if (!hasMenu) {
        // Simple toggle button
        return {'aria-pressed': isSelected}
      } else {
        // Menu trigger button
        const attributes: Record<string, string | boolean | undefined> = {
          'aria-haspopup': 'menu',
          'aria-expanded': isMenuOpen,
        }

        return attributes
      }
    }

    // Determine what text to display
    // For menu buttons, selectedItemLabel shows the selected menu item
    // For regular toggle buttons, children is always used
    const displayText = hasMenu && selectedItemLabel ? selectedItemLabel : children

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      if (onClick) {
        onClick(event)
      }
    }

    return (
      <button
        ref={ref}
        type="button"
        className={clsx(styles.toggleButton, isSelected && styles.selected)}
        onClick={handleClick}
        {...getAriaAttributes()}
      >
        {icon && <span className={styles.icon}>{icon}</span>}
        <span className={styles.textContent}>{displayText}</span>
      </button>
    )
  },
)

ToggleButton.displayName = 'ToggleButton'
